import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import AddressAutocomplete from "@/components/admin/AddressAutocomplete";
import { Navigation, Clock, MapPin, Loader2, Home, PenLine, LocateFixed } from "lucide-react";

interface RouteMapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientAddress: string;
  clientName: string;
}

interface Coords {
  lat: number;
  lng: number;
}

type LocationMode = "select" | "loading" | "ready";

const HOME_ADDRESS = "Rua Indiveri Colucci 14, Paço de Arcos, Oeiras, Portugal";

const RouteMapDialog = ({ open, onOpenChange, clientAddress, clientName }: RouteMapDialogProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [locationMode, setLocationMode] = useState<LocationMode>("select");
  const [manualAddress, setManualAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [travelInfo, setTravelInfo] = useState<{ distance: string; duration: string } | null>(null);
  const [adminCoords, setAdminCoords] = useState<Coords | null>(null);
  const [clientCoords, setClientCoords] = useState<Coords | null>(null);
  const [adminLabel, setAdminLabel] = useState("");

  // Reset to selection when dialog opens
  useEffect(() => {
    if (open) {
      setLocationMode("select");
      setManualAddress("");
      setError(null);
      setTravelInfo(null);
      setAdminCoords(null);
      setClientCoords(null);
      setAdminLabel("");
    }
  }, [open]);

  const geocode = async (address: string): Promise<Coords | null> => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&addressdetails=1&countrycodes=pt`,
        { headers: { "Accept-Language": "pt" } }
      );
      const data = await res.json();
      if (data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
      return null;
    } catch {
      return null;
    }
  };

  const getRoute = async (from: Coords, to: Coords) => {
    try {
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`
      );
      const data = await res.json();
      if (data.routes && data.routes.length > 0) {
        return data.routes[0];
      }
      return null;
    } catch {
      return null;
    }
  };

  const buildMap = async (adminPos: Coords, label: string) => {
    setLoading(true);
    setError(null);
    setTravelInfo(null);
    setAdminLabel(label);
    setLocationMode("ready");

    const clientPos = await geocode(clientAddress);
    if (!clientPos) {
      setError("Não foi possível encontrar o endereço do cliente no mapa.");
      setLoading(false);
      return;
    }

    setAdminCoords(adminPos);
    setClientCoords(clientPos);

    const route = await getRoute(adminPos, clientPos);

    if (route) {
      const distKm = (route.distance / 1000).toFixed(1);
      const durMin = Math.round(route.duration / 60);
      const hours = Math.floor(durMin / 60);
      const mins = durMin % 60;
      setTravelInfo({
        distance: `${distKm} km`,
        duration: hours > 0 ? `${hours}h ${mins}min` : `${mins} min`,
      });
    }

    // Load Leaflet
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    const L = await import("leaflet");
    await new Promise((r) => setTimeout(r, 100));

    if (!mapRef.current) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current);
    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
    }).addTo(map);

    // Icons: A = admin origin (green, start), B = client destination (red, end)
    const adminIcon = L.divIcon({
      html: '<div style="background:hsl(160,30%,40%);width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:bold;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">A</div>',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      className: "",
    });

    const clientIcon = L.divIcon({
      html: '<div style="background:hsl(345,55%,28%);width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:bold;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">B</div>',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      className: "",
    });

    L.marker([adminPos.lat, adminPos.lng], { icon: adminIcon })
      .addTo(map)
      .bindPopup(`<strong>A, Partida</strong><br/>${label}`);

    L.marker([clientPos.lat, clientPos.lng], { icon: clientIcon })
      .addTo(map)
      .bindPopup(`<strong>B, Destino</strong><br/>${clientName}<br/>${clientAddress}`);

    if (route && route.geometry) {
      const routeCoords = route.geometry.coordinates.map((c: number[]) => [c[1], c[0]]);
      L.polyline(routeCoords, {
        color: "hsl(345, 55%, 28%)",
        weight: 5,
        opacity: 0.8,
      }).addTo(map);
    }

    const bounds = L.latLngBounds(
      [adminPos.lat, adminPos.lng],
      [clientPos.lat, clientPos.lng]
    );
    map.fitBounds(bounds, { padding: [50, 50] });

    setLoading(false);
  };

  const handleHome = async () => {
    setLocationMode("loading");
    const coords = await geocode(HOME_ADDRESS);
    if (coords) {
      buildMap(coords, "Casa, " + HOME_ADDRESS);
    } else {
      setError("Não foi possível geocodificar o endereço de casa.");
      setLocationMode("select");
    }
  };

  const handleGPS = async () => {
    setLocationMode("loading");
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
      });
      buildMap({ lat: pos.coords.latitude, lng: pos.coords.longitude }, "Localização GPS");
    } catch {
      setError("GPS indisponível. Escolha outra opção.");
      setLocationMode("select");
    }
  };

  const handleManualSubmit = async () => {
    if (!manualAddress.trim()) return;
    setLocationMode("loading");
    const coords = await geocode(manualAddress);
    if (coords) {
      buildMap(coords, manualAddress);
    } else {
      setError("Não foi possível encontrar esse endereço.");
      setLocationMode("select");
    }
  };

  // Cleanup map on close
  useEffect(() => {
    if (!open && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Navigation size={20} className="text-primary" />
            Rota até {clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-4">
          {/* Location selection step */}
          {locationMode === "select" && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">Onde está agora?</p>
              <div className="grid gap-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3 rounded-xl"
                  onClick={handleHome}
                >
                  <Home size={18} className="text-primary shrink-0" />
                  <div className="text-left">
                    <p className="font-medium text-sm">Em casa</p>
                    <p className="text-xs text-muted-foreground">Rua Indiveri Colucci, 14, Paço de Arcos</p>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3 rounded-xl"
                  onClick={handleGPS}
                >
                  <LocateFixed size={18} className="text-primary shrink-0" />
                  <div className="text-left">
                    <p className="font-medium text-sm">Usar GPS</p>
                    <p className="text-xs text-muted-foreground">Detetar a minha localização atual</p>
                  </div>
                </Button>

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <PenLine size={12} />
                    Ou escreva outro endereço (com nº de porta):
                  </p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <AddressAutocomplete
                        value={manualAddress}
                        onChange={setManualAddress}
                        placeholder="Ex: Rua Augusta, 25, Lisboa"
                      />
                    </div>
                    <Button
                      onClick={handleManualSubmit}
                      disabled={!manualAddress.trim()}
                      size="default"
                    >
                      Ir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading state */}
          {locationMode === "loading" && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 size={20} className="animate-spin" />
                A obter localização...
              </div>
            </div>
          )}

          {/* Map view */}
          {locationMode === "ready" && (
            <>
              {/* Origin label */}
              {adminLabel && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <MapPin size={12} className="shrink-0" />
                  Partida: {adminLabel}
                  <button
                    onClick={() => setLocationMode("select")}
                    className="ml-auto text-xs text-primary underline hover:no-underline"
                  >
                    Alterar
                  </button>
                </p>
              )}

              {/* Travel info */}
              {travelInfo && (
                <div className="flex gap-4 rounded-xl bg-accent/50 border border-border p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Clock size={16} className="text-primary" />
                    <span>{travelInfo.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <MapPin size={16} className="text-primary" />
                    <span>{travelInfo.distance}</span>
                  </div>
                </div>
              )}

              {/* Client address */}
              <p className="text-sm text-muted-foreground flex items-start gap-2">
                <MapPin size={14} className="shrink-0 mt-0.5" />
                Destino: {clientAddress}
              </p>

              {/* Map */}
              <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: 400 }}>
                {loading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/80">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 size={20} className="animate-spin" />
                      A calcular rota...
                    </div>
                  </div>
                )}
                {error && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/80">
                    <p className="text-sm text-destructive text-center px-6">{error}</p>
                  </div>
                )}
                <div ref={mapRef} className="w-full h-full" />
              </div>

              {/* Google Maps */}
              {adminCoords && clientCoords && (
                <Button
                  variant="outline"
                  className="w-full rounded-full gap-2"
                  onClick={() => {
                    window.open(
                      `https://www.google.com/maps/dir/${adminCoords.lat},${adminCoords.lng}/${clientCoords.lat},${clientCoords.lng}`,
                      "_blank"
                    );
                  }}
                >
                  <Navigation size={14} />
                  Abrir no Google Maps
                </Button>
              )}
            </>
          )}

          {/* Error in select mode */}
          {locationMode === "select" && error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RouteMapDialog;
